/**
 * Adapter — client Session shape ↔ backend sessionHandler shape.
 *
 * The local Dexie repo uses camelCase names that mirror the recording
 * pipeline (id, startTime, endTime, track). The backend Lambda validates
 * a different naming convention (sessionId, date, startedAt, endedAt,
 * compressedTrack). Without this adapter every POST /sessions/sync
 * returns 400 BadRequest and every GET /sessions row is silently dropped
 * by the pull loop.
 */

/**
 * Convert a local session (as stored in Dexie / the mutation queue) into
 * the payload shape POST /sessions/sync expects.
 * @param {Object} local
 * @returns {Object}
 */
export function toApiPayload(local) {
  if (!local) return {};
  const startedAt = toIso(local.startTime);
  const endedAt = local.endTime != null ? toIso(local.endTime) : null;
  const date = startedAt ? startedAt.slice(0, 10) : null;
  const compressedTrack = local.track ? JSON.stringify(local.track) : undefined;
  return {
    sessionId: local.id,
    resortId: local.resortId ?? null,
    date,
    startedAt,
    endedAt,
    stats: local.stats,
    segments: local.segments || [],
    conditions: local.conditions ?? null,
    notes: local.notes ?? null,
    photos: local.photos || [],
    compressedTrack,
  };
}

/**
 * Convert a server row (as returned by GET /sessions or /sessions/{id})
 * into the local Dexie shape used everywhere on the client.
 * @param {Object} remote
 * @returns {Object|null}
 */
export function fromApiRow(remote) {
  if (!remote || (!remote.sessionId && !remote.id)) return null;
  const id = remote.sessionId || remote.id;
  const startTime = remote.startedAt ? toMs(remote.startedAt) : remote.startTime || null;
  const endTime = remote.endedAt ? toMs(remote.endedAt) : remote.endTime || null;
  return {
    id,
    resortId: remote.resortId ?? null,
    resortName: remote.resortName ?? null,
    status: remote.status || 'completed',
    startTime,
    endTime,
    stats: remote.stats || {},
    segments: remote.segments || [],
    conditions: remote.conditions ?? null,
    notes: remote.notes ?? null,
    photos: remote.photos || [],
    createdAt: remote.createdAt || null,
    updatedAt: remote.updatedAt || null,
  };
}

function toIso(v) {
  if (v == null) return null;
  if (typeof v === 'string') return v;
  try {
    return new Date(v).toISOString();
  } catch {
    return null;
  }
}

function toMs(v) {
  if (v == null) return null;
  if (typeof v === 'number') return v;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}
